import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../components/AuthContext';
import toast from 'react-hot-toast';
import { LogIn, Mail, Lock, Eye, EyeOff, Shield, Zap, Stethoscope, MessageCircle } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { login, isAuthenticating, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldUseNativeValidation: false,
    defaultValues: { email: '', password: '' }
  });

  const onSubmit = async (data, event) => {
    if (isSubmitting) return false;
    if (event) { event.preventDefault(); event.stopPropagation(); }

    setIsSubmitting(true);
    clearError();

    try {
      const result = await login(data.email, data.password, turnstileToken);
      if (result.success) {
        toast.success('Login successful!');
        reset();
        navigate(from, { replace: true });
      } else {
        toast.error(result.error || 'Login failed. Please check your credentials.', { duration: 5000, position: 'top-center' });
        setIsSubmitting(false);
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        return false;
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.', { duration: 5000, position: 'top-center' });
      setIsSubmitting(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      return false;
    }
  };

  return (
    <div className="min-h-screen flex font-body" style={{ background: '#f9f9fd', color: '#191c1e' }}>

      {/* ── Left branding panel ── */}
      <div className="hidden lg:flex lg:w-[44%] relative overflow-hidden flex-col justify-between p-12" style={{ background: 'linear-gradient(135deg,#00478d 0%,#005eb8 55%,#007072 100%)' }}>
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(151,242,243,0.15),transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full blur-[80px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(0,71,141,0.3),transparent 70%)' }} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
            <Stethoscope className="w-4 h-4 text-white" />
          </div>
          <span className="font-headline font-bold text-lg text-white tracking-tight">
            Sympto<span style={{ color: '#97f2f3' }}>Connect</span>
          </span>
        </div>

        {/* Center content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-sm">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-8 w-fit" style={{ background: 'rgba(151,242,243,0.2)', color: '#97f2f3' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#97f2f3]" />
            Clinical Serenity Platform
          </div>
          <h2 className="font-headline font-extrabold text-white mb-4 leading-[1.1]" style={{ fontSize: '2.25rem', letterSpacing: '-0.02em' }}>
            Welcome back<br />to your care hub.
          </h2>
          <p className="text-base leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Securely connect with your healthcare team, track symptoms, and get real-time clinical insights — all in one place.
          </p>

          {/* Feature list */}
          <div className="space-y-3">
            {[
              { Icon: Shield, label: 'End-to-end encrypted' },
              { Icon: Zap, label: 'Real-time care intelligence' },
              { Icon: Stethoscope, label: 'Verified clinician network' },
              { Icon: MessageCircle, label: 'Private care messaging' },
            ].map(({ Icon, label }, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(8px)' }}>
                <div className="w-7 h-7 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(151,242,243,0.2)' }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: '#97f2f3' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { value: '50K+', label: 'Active patients' },
            { value: '98%', label: 'Detection rate' },
            { value: '4.9★', label: 'Satisfaction' },
          ].map((s, i) => (
            <div key={i}>
              <p className="font-headline font-extrabold text-xl text-white">{s.value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12" style={{ background: '#f9f9fd' }}>
        <div className="w-full max-w-[400px]">

          {/* Mobile logo */}
          <Link to="/" className="flex lg:hidden items-center justify-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            <span className="font-headline font-bold text-lg tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </Link>

          {/* Heading */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#727783' }}>Patient & Clinician Portal</p>
            <h1 className="font-headline font-extrabold mb-2" style={{ fontSize: '1.875rem', letterSpacing: '-0.02em', color: '#191c1e' }}>
              Sign in to your account
            </h1>
            <p className="text-sm" style={{ color: '#424752' }}>
              Welcome back — enter your details to continue.
            </p>
          </div>

          {/* ── Demo quick-fill ── */}
          <div className="mb-6 p-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Try a demo account</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Doctor', email: 'dr.chen@symptoconnect.com', password: 'doctor123', color: '#00478d' },
                { label: 'Patient', email: 'alice@example.com',          password: 'password123', color: '#007072' },
                { label: 'Caregiver', email: 'caregiver@example.com',   password: 'caregiver123', color: '#5f4300' },
              ].map(({ label, email, password, color }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => { setValue('email', email); setValue('password', password); }}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-80"
                  style={{ background: color }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <form
            className="space-y-5"
            onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleSubmit(onSubmit)(e); }}
          >
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold mb-1.5" style={{ color: '#424752' }}>
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4" style={{ color: '#727783' }} />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="block w-full pl-10 pr-4 py-3 text-sm rounded-xl outline-none transition-all"
                  style={{
                    background: errors.email ? '#fff0f0' : '#e2e2e6',
                    color: '#191c1e',
                    border: errors.email ? '2px solid #ba1a1a' : '2px solid transparent',
                  }}
                  onFocus={e => { e.target.style.background = '#ffffff'; e.target.style.border = '2px solid #00478d'; }}
                  onBlur={e => { e.target.style.background = errors.email ? '#fff0f0' : '#e2e2e6'; e.target.style.border = errors.email ? '2px solid #ba1a1a' : '2px solid transparent'; }}
                  placeholder="you@example.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }
                  })}
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs font-semibold" style={{ color: '#ba1a1a' }}>{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="text-sm font-semibold" style={{ color: '#424752' }}>Password</label>
                <Link to="/forgot-password" className="text-xs font-bold transition-colors" style={{ color: '#00478d' }}>
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4" style={{ color: '#727783' }} />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="block w-full pl-10 pr-11 py-3 text-sm rounded-xl outline-none transition-all"
                  style={{
                    background: errors.password ? '#fff0f0' : '#e2e2e6',
                    color: '#191c1e',
                    border: errors.password ? '2px solid #ba1a1a' : '2px solid transparent',
                  }}
                  onFocus={e => { e.target.style.background = '#ffffff'; e.target.style.border = '2px solid #00478d'; }}
                  onBlur={e => { e.target.style.background = errors.password ? '#fff0f0' : '#e2e2e6'; e.target.style.border = errors.password ? '2px solid #ba1a1a' : '2px solid transparent'; }}
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                  })}
                />
                <button type="button" className="absolute inset-y-0 right-0 pr-3.5 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword
                    ? <EyeOff className="h-4 w-4 transition-colors" style={{ color: '#727783' }} />
                    : <Eye className="h-4 w-4 transition-colors" style={{ color: '#727783' }} />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-xs font-semibold" style={{ color: '#ba1a1a' }}>{errors.password.message}</p>}
            </div>

            {/* Turnstile invisible bot protection */}
            <Turnstile
              ref={turnstileRef}
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              options={{ size: 'invisible' }}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={isAuthenticating || isSubmitting || !turnstileToken}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-full font-bold text-white text-sm min-h-[48px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)', boxShadow: '0px 4px 16px rgba(0,71,141,0.25)' }}
            >
              {isAuthenticating || isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Sign In
                </>
              )}
            </button>

            <p className="text-center text-sm pt-1" style={{ color: '#424752' }}>
              Don't have an account?{' '}
              <Link to="/register" className="font-bold transition-colors" style={{ color: '#00478d' }}>
                Create account
              </Link>
            </p>
          </form>

          {/* Back link */}
          <div className="mt-8 text-center">
            <Link to="/welcome" className="text-xs font-semibold transition-colors" style={{ color: '#727783' }}>
              ← Back to Welcome
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;